import { IconRemove } from '@newfront-insurance/core-ui';
import { Button } from '@newfront-insurance/core-ui/v2';
import { useMemo, useState } from 'react';

import { AddPeopleDialog } from './add-people-dialog';
import { useAddCustomSkillShares, useCustomSkillShares } from './use-custom-skill-shares';
import { useRemoveCustomSkillShare } from './use-remove-custom-skill-share';
import type { CustomSkillRecord, CustomSkillShareRecord } from '../../../../../queries/chats';
import { SettingsConfirmationDialog } from '../../settings-confirmation-dialog';
import { getEmployeeDisplayName, getEmployeeInitials } from '../../settings-people/employee-utils';
import { SettingsPersonRow } from '../../settings-person-row';
import { SettingsSectionList } from '../../settings-section-list';
import { SettingsActionButton } from '../../settings-shared/settings-action-button';

export function CustomSkillSharesSummary({
  skill,
  isAddingPeople,
  onAddPeople,
  onCancelAddPeople,
  onChanged,
}: {
  skill: CustomSkillRecord;
  isAddingPeople: boolean;
  onAddPeople: () => void;
  onCancelAddPeople: () => void;
  onChanged: () => Promise<void>;
}) {
  const { shares, sharedEmployeesByUserUuid, isLoading, reloadShares } = useCustomSkillShares(skill);
  const removal = useRemoveCustomSkillShare(skill, reloadShares, onChanged);
  const pendingRemoveUserUuid = removal.pendingKey;
  const [removing, setRemoving] = useState<{ share: CustomSkillShareRecord; name: string } | null>(null);
  const excludedShareUserUuids = useMemo(() => new Set(shares.map((share) => share.userUuid)), [shares]);
  const handleAddSelectedPeople = useAddCustomSkillShares({ skill, reloadShares, onChanged });
  const shareCountLabel =
    shares.length === 0
      ? 'Not shared with people'
      : `Shared with ${shares.length} ${shares.length === 1 ? 'person' : 'people'}`;

  return (
    <>
      <SettingsSectionList
        title={shareCountLabel}
        loading={isLoading}
        emptyMessage={shares.length === 0 && !isLoading ? 'No people have direct access to this skill.' : undefined}
        headerEnd={
          <Button
            type="button"
            variant="tertiary"
            className="h-auto shrink-0 p-0 text-sm leading-4"
            disabled={Boolean(pendingRemoveUserUuid)}
            onClick={onAddPeople}
          >
            + Add people
          </Button>
        }
      >
        {shares.length
          ? shares.map((share) => {
              const sharedEmployee = sharedEmployeesByUserUuid.get(share.userUuid);
              const sharedEmployeeName = sharedEmployee ? getEmployeeDisplayName(sharedEmployee) : undefined;
              const isRemovingShare = pendingRemoveUserUuid === share.userUuid;
              return (
                <SettingsPersonRow
                  key={share.userUuid}
                  name={sharedEmployeeName}
                  description={sharedEmployee?.email}
                  imageUrl={sharedEmployee?.imageUrl}
                  initials={getEmployeeInitials(sharedEmployee, sharedEmployeeName ?? '')}
                  loadingLabel={sharedEmployee ? undefined : 'Loading shared user'}
                  action={
                    <SettingsActionButton
                      label={sharedEmployeeName ? `Remove ${sharedEmployeeName}` : 'Remove shared user'}
                      tooltip="Unshare"
                      disabled={Boolean(pendingRemoveUserUuid)}
                      ariaBusy={isRemovingShare}
                      onClick={() => {
                        removal.clearError();
                        setRemoving({ share, name: sharedEmployeeName ?? 'this person' });
                      }}
                    >
                      <span aria-hidden="true">
                        <IconRemove color="currentColor" size={16} />
                      </span>
                    </SettingsActionButton>
                  }
                />
              );
            })
          : null}
      </SettingsSectionList>
      {removing ? (
        <SettingsConfirmationDialog
          title="Revoke skill access"
          description={
            <>
              Stop sharing /{skill.command} directly with {removing.name}? They may still have access through a team or
              organization.
            </>
          }
          confirmLabel="Revoke access"
          pendingLabel="Revoking…"
          pending={Boolean(pendingRemoveUserUuid)}
          error={removal.error}
          onClose={() => setRemoving(null)}
          onConfirm={() =>
            void removal.removeUser(removing.share.userUuid).then((succeeded) => {
              if (succeeded) setRemoving(null);
            })
          }
        />
      ) : null}
      <AddPeopleDialog
        open={isAddingPeople}
        excludedUserUuids={excludedShareUserUuids}
        onClose={onCancelAddPeople}
        onAddPeople={handleAddSelectedPeople}
      />
    </>
  );
}
